import {
  type Abundance,
  allWholeNumbers,
  totalOf,
} from "@/lib/tool-kit/ecology/abundance";

/**
 * The Shannon (Shannon–Wiener) diversity index from species counts.
 *
 * H′ = −Σ pᵢ log_b pᵢ with pᵢ = nᵢ ÷ N. The base b only sets the unit — e gives nats
 * (the ecologists' usual ln), 2 gives bits, 10 gives hartleys — and does not change
 * which community is more diverse.
 *
 * - Maximum: H′max = log_b S, reached when all S species are equally common.
 * - Pielou's evenness: J′ = H′ ÷ H′max, between 0 and 1.
 * - Effective number of species (Hill number of order 1): exp(H′ in nats), the same
 *   for every base: b^(H′ in base b). It is how many equally common species would give
 *   the same H′.
 * - Miller–Madow bias correction: H′ + (S − 1) ÷ (2N) in nats, for whole-number counts.
 * - Variance of H′ (Hutcheson 1970, after Basharin 1959): in nats,
 *   (Σ p ln²p − (Σ p ln p)²) ÷ N + (S − 1) ÷ (2N²).
 */

export type LogBase = "e" | "2" | "10";

export const BASE_VALUE: Record<LogBase, number> = {
  e: Math.E,
  "2": 2,
  "10": 10,
};

/** ln of the base: the divisor that converts nats to the chosen unit. */
export function lnBase(base: LogBase): number {
  return Math.log(BASE_VALUE[base]);
}

export interface ShannonTerm {
  label: string;
  count: number;
  /** pᵢ. */
  share: number;
  /** log_b pᵢ. */
  logShare: number;
  /** −pᵢ log_b pᵢ, the species' contribution to H′. */
  term: number;
}

export interface ShannonResult {
  richness: number;
  total: number;
  base: LogBase;
  /** H′ in the chosen unit. */
  entropy: number;
  /** log_b S. */
  maxEntropy: number;
  /** J′, or null for a single species (0 ÷ 0). */
  evenness: number | null;
  /** exp(H′ in nats). */
  effective: number;
  /** Effective number ÷ S. */
  effectiveEvenness: number;
  /** Miller–Madow corrected H′ in the chosen unit; null unless counts are whole. */
  corrected: number | null;
  /** Approximate standard error of H′ in the chosen unit; null unless counts are whole. */
  standardError: number | null;
  terms: ShannonTerm[];
}

/** The variance of H′ in nats, Hutcheson's approximation, from the shares. */
export function entropyVariance(
  shares: readonly number[],
  total: number
): number {
  let first = 0;
  let second = 0;
  for (const p of shares) {
    const lnP = Math.log(p);
    first += p * lnP;
    second += p * lnP * lnP;
  }
  return (
    (second - first * first) / total + (shares.length - 1) / (2 * total * total)
  );
}

export function shannon(
  entries: readonly Abundance[],
  base: LogBase
): ShannonResult | null {
  const total = totalOf(entries);
  if (entries.length === 0 || !(total > 0)) {
    return null;
  }
  const unit = lnBase(base);
  const richness = entries.length;
  const terms = entries.map((entry): ShannonTerm => {
    const share = entry.count / total;
    const logShare = Math.log(share) / unit;
    return {
      label: entry.label,
      count: entry.count,
      share,
      logShare,
      term: -share * logShare,
    };
  });
  let entropy = 0;
  for (const term of terms) {
    entropy += term.term;
  }
  // A single species gives exactly 0, never a rounding residue.
  if (richness === 1) {
    entropy = 0;
  }
  const maxEntropy = Math.log(richness) / unit;
  const nats = entropy * unit;
  const whole = allWholeNumbers(entries);
  const variance = whole
    ? entropyVariance(
        terms.map((term) => term.share),
        total
      )
    : null;
  const effective = Math.exp(nats);
  return {
    richness,
    total,
    base,
    entropy,
    maxEntropy,
    evenness: richness > 1 ? entropy / maxEntropy : null,
    effective,
    effectiveEvenness: effective / richness,
    corrected: whole ? (nats + (richness - 1) / (2 * total)) / unit : null,
    standardError: variance === null ? null : Math.sqrt(variance) / unit,
    terms,
  };
}
